import React, { useRef, useEffect } from "react";
import { Button, Card, Form, Input, Select, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import RequiredFormItem from "./RequiredFormItem";
import { useGetUserInfoQuery, useUpdateUserInfoMutation } from "../../services/account.service";
import { getUserData } from "../../services/auth.service";
import countryData from "../../statics/countries.json";
import { EXPERIENCE_LEVEL_OPTIONS } from "./constants";

const ProfilePage = () => {
    const location = useLocation();
    const { state } = location;
    const navigate = useNavigate();
    const { data: userInfo } = useGetUserInfoQuery();
    const [updateUserInfo, { isLoading }] = useUpdateUserInfoMutation();
    const user = userInfo?.data || getUserData();
    const paypalEmailRef = useRef(null);

    const getRuleMessage = (fieldName) => {
        return `${fieldName} is required`;
    };

    useEffect(() => {
        if (state?.highlightPaypalEmail && paypalEmailRef.current) {
            const element = paypalEmailRef.current;

            element.scrollIntoView({ behavior: "smooth", block: "center" });

            setTimeout(() => {
                const input = element.querySelector("input");
                if (input) {
                    input.focus();
                }

                element.classList.add("highlight-field");

                setTimeout(() => {
                    element.classList.remove("highlight-field");
                }, 3000);
            }, 500);
        }
    }, [state]);

    const handleSubmitForm = async (values) => {
        const payload = {
            name: values.name,
            email: values.email,
            business_name: values.business_name,
            phone: values.phone,
            paypal_email: values.paypal_email,
            country: values.country,
            experience: values.experience,
            main_channel: values.main_channel,
            promote_plan: values.promote_plan,
            message: values.message,
        };
        try {
            const result = await updateUserInfo(payload).unwrap();
            if (!result.success) {
                throw new Error(result.message || "Failed to update profile");
            }
            message.success("Profile updated successfully");
            if (state?.highlightPaypalEmail) {
                navigate("/");
            }
        } catch (error) {
            message.error(error?.message || "Failed to update profile");
        }
    };

    return (
        <Card bordered bodyStyle={{ padding: "1rem" }} className="ProfilePage">
            <h2 className="ProfilePage__Title">Profile</h2>

            <Form
                className="ProfilePageForm"
                layout="vertical"
                initialValues={{
                    name: user?.name || "",
                    email: user?.email || "",
                    paypal_email: user?.paypal_email || "",
                    phone: user?.business_profile?.phone || "",
                    country: user?.business_profile?.country || "",
                    experience: user?.business_profile?.experience || "",
                    main_channel: user?.business_profile?.main_channel || "",
                    promote_plan: user?.business_profile?.promote_plan || "",
                    message: user?.business_profile?.message || "",
                }}
                onFinish={handleSubmitForm}
            >
                <RequiredFormItem
                    name="name"
                    label="Full Name"
                    rules={[
                        { required: true, message: getRuleMessage("Full Name") },
                        { min: 2, message: "Full Name must be at least 2 characters" },
                        { max: 100, message: "Full Name must be at most 100 characters" },
                    ]}
                >
                    <Input placeholder="e.g. John Doe" />
                </RequiredFormItem>

                <RequiredFormItem
                    name="email"
                    label="Email"
                    rules={[{ required: true, message: getRuleMessage("Email") }]}
                >
                    <Input placeholder="e.g. johndoe@gmail.com" disabled />
                </RequiredFormItem>

                <RequiredFormItem
                    name="phone"
                    label="Phone number"
                    rules={[{ required: true, message: getRuleMessage("Phone number") }]}
                >
                    <Input placeholder="+84987654321" />
                </RequiredFormItem>

                <div ref={paypalEmailRef}>
                    <RequiredFormItem
                        name="paypal_email"
                        label="Paypal Email (for receiving payment)"
                        rules={[
                            { required: true, message: getRuleMessage("Paypal Email") },
                            {
                                type: "email",
                                message: "Paypal Email is not valid Email",
                            },
                        ]}
                    >
                        <Input />
                    </RequiredFormItem>
                </div>

                <RequiredFormItem
                    name="main_channel"
                    label="Main Channel"
                    rules={[{ required: true, message: getRuleMessage("Main Channel") }]}
                >
                    <Input placeholder="e.g. your @handle, link to your profile..." />
                </RequiredFormItem>

                <RequiredFormItem
                    name="country"
                    label="Country"
                    rules={[{ required: true, message: getRuleMessage("Country") }]}
                >
                    <Select
                        placeholder="Select your country"
                        options={countryData.countries.map((country) => ({
                            label: country.name,
                            value: country.code,
                        }))}
                        showSearch
                        optionFilterProp="label"
                    />
                </RequiredFormItem>

                <RequiredFormItem name="experience" label="Experience">
                    <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="e.g. 2 years in affiliate marketing"
                        options={EXPERIENCE_LEVEL_OPTIONS}
                    />
                </RequiredFormItem>

                <RequiredFormItem
                    name="message"
                    label="How do you plan to promote [Merchize]?"
                    rules={[
                        { required: true, message: getRuleMessage("Promotion plan") },
                        {
                            max: 2000,
                            message: "Promotion plan must be at most 2000 characters",
                        },
                    ]}
                >
                    <Input.TextArea
                        placeholder="e.g. Plans for promotion, preferred contact method, or anything else you'd like us to know."
                        style={{ height: 96 }}
                    />
                </RequiredFormItem>
                <Button className="ProfilePageForm__SubmitButton" type="primary" htmlType="submit" loading={isLoading}>
                    Update profile
                </Button>
            </Form>
        </Card>
    );
};

export default ProfilePage;
